'use client';

import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { CatSearchForm } from '@blocks-react/components/SearchForm';
import { useController, useFormContext } from 'react-hook-form';
import { MyEquipmentSearchFormProps } from '../types';
import styles from '../MyEquipment.module.scss';
import { BlocksSearchForm } from '@blocks-web-components/components/search-form/search-form';

export const MyEquipmentSearchForm: FC<
  PropsWithChildren<MyEquipmentSearchFormProps>
> = ({
  children,
  placeholder,
  maxLength,
  onSearch,
  onSearchClose,
  rules = {},
  name,
  classname,
  isEmpty = false,
  isError,
  showDuplicateEquipmentError,
  onBlMobileOverlayOpen,
  onBlMobileOverlayClose,
  dataCsMask,
  mobileBreakPoint = 560,
  defaultEquipmentModel,
  activeIndex,
  setDefaultEquipmentModel
}) => {
  const { control, setValue, getValues } = useFormContext();
  const searchFormRef = useRef<BlocksSearchForm | null>(null);
  const { field, fieldState } = useController({ name, control, rules });
  const error = !!(isError ?? fieldState.error) || showDuplicateEquipmentError;
  const onMobileOverlayOpen = () => {
    if (onBlMobileOverlayOpen) {
      onBlMobileOverlayOpen();
    }
  };

  const onMobileOverlayClose = () => {
    if (onBlMobileOverlayClose) {
      onBlMobileOverlayClose();
    }
  };

  useEffect(() => {
    const ref = searchFormRef.current;
    if (!ref) return;

    const observer = new MutationObserver(() => {
      const input = ref.shadowRoot?.querySelector('input');
      if (input && defaultEquipmentModel && activeIndex) {
        input.click();
        observer.disconnect();
      }
    });
    if (ref.shadowRoot && defaultEquipmentModel && activeIndex) {
      observer.observe(ref.shadowRoot, {
        childList: true,
        subtree: true
      });
    }

    return () => {
      observer.disconnect();
      setDefaultEquipmentModel && setDefaultEquipmentModel(undefined);
    };
  }, [defaultEquipmentModel, activeIndex, setDefaultEquipmentModel]);

  return (
    // TODO - Input Validation - Ensure that <,>,/,\ characters and <script> tags are not allowed to type/paste.
    // TODO - Input Validation - Ensure that maximum query params length is 1024 characters
    <CatSearchForm
      size="md"
      ref={searchFormRef}
      placeholder={placeholder}
      className={`${styles['searh-form-overlay']} ${classname}`}
      status={error ? 'error' : undefined}
      maxlength={maxLength}
      data-testid="searchForm"
      value={getValues(name)}
      onBlChange={(e) => {
        setValue(name, e.detail.value ?? '');
        onSearch(e.detail.value ?? '');
        field.onChange(e);
      }}
      onBlSearchClose={(e: Event) => {
        e.stopPropagation();
        if (onSearchClose) {
          onSearchClose();
        }
      }}
      onBlur={() => field.onBlur()}
      isEmpty={isEmpty}
      onBlMobileOverlayOpen={onMobileOverlayOpen}
      onBlMobileOverlayClose={onMobileOverlayClose}
      data-cs-encrypt={dataCsMask}
      mobileBreakPoint={mobileBreakPoint}
    >
      {children}
    </CatSearchForm>
  );
};
